import type { Editor } from '@tiptap/react';
import type { RxDocument } from 'rxdb';

import { AGENT_REQUEST_DOCUMENT_TYPES, type AgentRequestDocumentType } from '@flintmint/core-shared/constants';
import type { Note, Block, UserSource } from '@flintmint/core-shared/rxdb';
import { DEFAULT_NOTE_NAME } from '@/hooks/rxdb-models/use-notes';
import { DEFAULT_BLOCK_NAME } from '@/hooks/rxdb-models/use-blocks';

export type PersistedDocument = RxDocument<Note> | RxDocument<Block> | RxDocument<UserSource>;
export type AgentDocumentType = 'notes' | 'templates' | 'blocks' | 'userSources';

export const isNoteDocument = (doc: PersistedDocument): doc is RxDocument<Note> => 'variant' in doc;

export const isBlockDocument = (doc: PersistedDocument): doc is RxDocument<Block> => 'aliases' in doc && !('variant' in doc);

export const getAgentRequestDocumentFields = (
  document?: PersistedDocument
): { documentId?: string; documentType?: AgentRequestDocumentType } => {
  const documentType = AGENT_REQUEST_DOCUMENT_TYPES.find((type) => type === document?.collection.name);
  if (!document || !documentType) return {};

  return { documentId: document.id, documentType };
};

export const isDefaultDocumentName = (name: string): boolean => name === DEFAULT_NOTE_NAME || name === DEFAULT_BLOCK_NAME;

export const getEditorCharCount = (editor: Editor | null | undefined): number => editor?.state.doc.textContent?.length ?? 0;

export const getAgentDocumentType = (params: {
  document?: PersistedDocument;
  editor?: Editor | null;
}): AgentDocumentType | undefined => {
  const { document, editor } = params;
  const editorType = editor?.storage?.documentInfo?.editorType;

  if (editorType === 'block') return 'blocks';
  if (editorType === 'cortex') return 'userSources';

  if (!document) return undefined;

  if ('variant' in document) {
    const noteVariant = document.variant;
    if (noteVariant === 'notes' || noteVariant === 'templates') {
      return noteVariant;
    }
  }

  return undefined;
};
